import {
  useGetPrinters,
  usePingPrintDriverServer,
} from "./api/queries/usePrint";
import { usePrintMutation } from "./api/mutations/usePrint";
import { useContext, useState } from "react";
import { cleanObject } from "../helpers";
import { ToastContext } from "./context";

// 80mm thermal printer at 203 DPI = 576px usable width
// Margins on 80mm rolls typically consume ~2mm each side, leaving ~76mm printable
// 76mm at 203 DPI = 606px, but 576px is the widely accepted safe printable width
const PAPER_SIZES = {
  80: 576,
  50: 384,
};

export const usePrint = ({ isEnabled = false, isTest = false }) => {
  const [defaultPaper] = useState(
    localStorage.getItem("default-LB-printer-paper")
      ? JSON.parse(localStorage.getItem("default-LB-printer-paper"))
      : null,
  );
  const [defaultPrinter] = useState(
    localStorage.getItem("default-LB-printer")
      ? JSON.parse(localStorage.getItem("default-LB-printer"))
      : null,
  );
  const [useConfig] = useState(
    localStorage.getItem("use-LB-printer-config")
      ? JSON.parse(localStorage.getItem("use-LB-printer-config"))
      : true,
  );
  const { pingServer } = usePingPrintDriverServer();
  const { mutate: printViaServer } = usePrintMutation();
  const { data: printers } = useGetPrinters({
    isEnabled: isEnabled || false,
  });
  const triggerToast = useContext(ToastContext);

  // Resolve pixel width: prefer explicit id lookup, fallback to 80mm (576px)
  const paperSize = defaultPaper?.id
    ? (PAPER_SIZES[Number(defaultPaper.id)] ?? Number(defaultPaper.id))
    : PAPER_SIZES[80];

  const getPrinters = () => {
    return printers;
  };

  const print = async ({ onError = () => {}, ...params }) => {
    const isPingSuccessful = await pingServer();

    if (isPingSuccessful && useConfig)
      return printViaDriverServer({ onError, ...params });

    if (isTest !== true) return printViaWindows({ onError, ...params });

    triggerToast(
      "Sorry, I can't detect a succesful configuration. Please follow the listed steps",
    );
    onError();
  };

  const printViaDriverServer = ({
    imageData,
    onSuccess = () => {},
    onError = () => {},
  }) => {
    const data = cleanObject({
      imageData: imageData,
      thermalWidth: paperSize,
      printerName: defaultPrinter?.name || null,
      vendorId: defaultPrinter?.vendorId || null,
      productId: defaultPrinter?.productId || null,
      serialNumber: defaultPrinter?.serialNumber || null,
    });

    printViaServer(data, {
      onSuccess: () => {
        onSuccess();
      },
      onError: () => {
        onError();
      },
    });
  };

  const printViaWindows = ({ imageData, onSuccess = () => {} }) => {
    const iframe = document.createElement("iframe");
    document.body.appendChild(iframe);
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";

    iframe.contentDocument.write(`
      <html>
        <head>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { width: ${paperSize}px; }
            img {
              display: block;
              width: ${paperSize}px;
              height: auto;
            }
            @media print {
              @page { margin: 0; size: ${paperSize}px auto; }
              body { margin: 0; width: ${paperSize}px; }
            }
          </style>
        </head>
        <body>
          <img src="${imageData}" />
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    iframe.contentDocument.close();
    onSuccess();
  };

  return {
    paperSize,
    printerInfo: defaultPrinter || null,
    useConfig,
    pingServer,
    print,
    getPrinters,
    printViaWindows,
    printViaDriverServer,
  };
};
